<template>
  <v-card>
    <v-card-text>
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="Hatırlatıcılar"
            icon="mdi-alarm-multiple"
            @clear-filters="$refs.headSearch.reset"
            @reload="loadList"
            :hide-edit="!can('edit-notification')"
            :edit-enabled="selectedItems.length === 1"
            @click:edit="handleEditClick"
            :search.sync="search.query"
            v-bind="titleBarAttrs"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.edit="{ item }">
          <router-link
            class="btn btn-icon btn-sm btn-clean"
            :to="{
              name: 'communication.reminders.edit',
              params: { id: item.id },
            }"
          >
            <i class="menu-icon mdi mdi-pencil"></i>
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.subject="{ value, item }">
          <router-link
            :to="{
              name: 'communication.reminders.show',
              params: { id: item.id },
            }"
          >
            <rs-table-cell-string
              :value="value"
              :limit="30"
              :title="item.body"
            />
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.reminded_at="{ value }">
          <rs-table-cell-date show-time :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.is_closed="{ item, value }">
          <rs-table-cell-boolean
            :value="value"
            :title="item.closed_at || null"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.relation="{ item, value }">
          <router-link
            v-if="item.company_id"
            :to="{
              name: 'definitions.companies.edit',
              params: { id: item.company_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.cluster_id"
            :to="{
              name: 'definitions.clusters.edit',
              params: { id: item.cluster_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.house_id"
            :to="{
              name: 'definitions.houses.edit',
              params: { id: item.house_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.resident_id"
            :to="{
              name: 'definitions.residents.edit',
              params: { id: item.resident_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.provider_id"
            :to="{
              name: 'definitions.cluster-providers.edit',
              params: { id: item.provider_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.safe_id"
            :to="{
              name: 'definitions.safes.edit',
              params: { id: item.safe_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.bank_account_id"
            :to="{
              name: 'definitions.bank-accounts.edit',
              params: { id: item.bank_account_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.invoice_id"
            :to="{
              name: 'expenses.invoices.edit',
              params: { id: item.invoice_id },
            }"
          >
            {{ value }}
          </router-link>
          <router-link
            v-else-if="item.fixture_id"
            :to="{
              name: 'other-definitions.fixtures.fixtures.edit',
              params: { id: item.fixture_id },
            }"
          >
            {{ value }}
          </router-link>
          <template v-else-if="item.care_id">
            <router-link
              v-if="item.care_type === 1"
              :to="{
                name: 'communication.breakdowns.edit',
                params: { id: item.care_id },
              }"
            >
              {{ value }}
            </router-link>
            <router-link
              v-else-if="item.care_type === 2"
              :to="{
                name: 'communication.suggestions.edit',
                params: { id: item.care_id },
              }"
            >
              {{ value }}
            </router-link>
            <router-link
              v-else-if="item.care_type === 3"
              :to="{
                name: 'communication.complaints.edit',
                params: { id: item.care_id },
              }"
            >
              {{ value }}
            </router-link>
          </template>

          <template v-else>
            {{ value }}
          </template>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- <v-card-text v-else>
      Listeyi görmek için üst menüden bir toplu yaşam alanı seçin.
    </v-card-text> -->
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions } from "@/view/mixins";

export default {
  name: "ReminderList",
  mixins: [filtersToURL, hasExport, hasPermissions],
  computed: {
    ...mapGetters(["companyId", "clusterId", "reminderPeriodList", "typeList"]),
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }),
      deep: true,
    },
    clusterId() {
      this.loadList();
    },
    companyId() {
      this.loadList();
    },
  },
  data() {
    return {
      titleBarAttrs: {
        exportUrl: "reminder-schedules",
        exportParams: this.getParams,
      },
      options: {
        sortBy: ["reminded_at"],
        sortDesc: [false],
      },
      search: {
        is_closed: false,
      },
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
          hide: () => !this.can("edit-notification"),
        },
        {
          text: "Konu",
          value: "subject",
          searchable: "text",
        },
        {
          text: "Tarih",
          value: "reminded_at",
          searchable: "date",
        },
        {
          text: "İlişki Türü",
          value: "relation_type",
          searchable: "multiselect",
          options: () => this.typeList,
          sortable: false,
        },
        {
          text: "İlişki",
          value: "relation",
          searchable: "text",
        },
        {
          text: "Periyot",
          value: "reminder_period",
          searchable: "multiselect",
          options: () => this.reminderPeriodList,
          searchValue: "reminder_period_id",
        },
        {
          text: "Kapanmış",
          value: "is_closed",
          searchable: "yesNo",
          align: "center",
          width: "80px",
        },
      ],
    };
  },
  methods: {
    ...mapActions(["updateReminderCount"]),
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      if (this.clusterId) {
        params.by_cluster_id = this.clusterId;
      }

      if (this.companyId) {
        params.by_company_id = this.companyId;
      }

      params.is_reminded = true;

      return params;
    },
    loadList() {
      if (this.isLoading) {
        this.resetTable();
        return false;
      }

      this.resetTable();
      this.isLoading = true;

      const params = this.getParams();

      this.$api
        .query("reminder-schedules", { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
            this.footTotals = response.data.meta.totals;

            if (this.search.is_closed === false) {
              this.updateReminderCount(response.data.meta.total);
            }
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleEditClick() {
      if (this.isLoading || this.selectedItems.length !== 1) {
        return false;
      }

      this.$router.push({
        name: "communication.reminders.edit",
        params: {
          id: this.selectedItems[0].id,
        },
      });
    },
  },
  mounted() {
    this.generateExportColumns(this.headers);
  },
};
</script>
